import React, { useState, useEffect, useCallback, useRef } from 'react';
import { requestReceiptOcrAI, checkAiServerHealth } from '../lib/aiServiceClient';
import { parseReceiptText, downloadTextFile } from '../lib/receiptOcrHelper';

export const ReceiptOcrModal = ({
  isOpen,
  onClose,
  imageUrl,
  onApplyData,
  supplierSuggestions = [],
  engine
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('');
  const [scanError, setScanError] = useState('');
  const [confidence, setConfidence] = useState(null);

  // AI Server and Engine states
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking' | 'online' | 'offline'
  const [selectedEngine, setSelectedEngine] = useState('auto'); // 'auto' | 'server' | 'local'
  const [usedEngine, setUsedEngine] = useState(engine || null); // 'server' | 'local_server' | 'local' | 'local_fallback' | 'gemini_spark' | null

  const [extractedData, setExtractedData] = useState({
    receiptNumber: '',
    poNumber: '',
    supplierName: '',
    receivedDate: '',
    totalAmount: '',
    detectedItems: []
  });
  const [rawText, setRawText] = useState('');
  const [activeTab, setActiveTab] = useState('raw'); // 'raw' | 'items'
  const [isZoomed, setIsZoomed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const supplierSuggestionsRef = useRef(supplierSuggestions);
  useEffect(() => {
    supplierSuggestionsRef.current = supplierSuggestions;
  }, [supplierSuggestions]);

  // Check AI server health on modal open
  useEffect(() => {
    if (isOpen) {
      let isMounted = true;
      setServerStatus('checking');
      checkAiServerHealth(2500)
        .then((res) => {
          if (isMounted) {
            setServerStatus(res.isOnline ? 'online' : 'offline');
          }
        })
        .catch(() => {
          if (isMounted) {
            setServerStatus('offline');
          }
        });
      return () => {
        isMounted = false;
      };
    }
  }, [isOpen]);

  // Trigger OCR scan
  const startScan = useCallback(async () => {
    if (!imageUrl) {
      setScanError('ไม่พบรูปภาพใบเสร็จสำหรับประมวลผล');
      return;
    }

    setIsScanning(true);
    setScanError('');
    setProgress(5);
    setProgressStatus('กำลังเตรียมภาพ...');

    try {
      const result = await requestReceiptOcrAI(imageUrl, {
        onProgress: ({ status, progress: pct }) => {
          setProgress(pct);
          setProgressStatus(status);
        },
        forceEngine: selectedEngine
      });

      setRawText(result.rawText || '');
      setConfidence(result.confidence);
      setUsedEngine(result.engine);

      // If supplier suggestions are provided and supplier is missing, try regex matching
      let supplierName = result.supplierName || '';
      if (!supplierName && supplierSuggestionsRef.current?.length > 0 && result.rawText) {
        const parsed = parseReceiptText(result.rawText, supplierSuggestionsRef.current);
        if (parsed.supplierName) {
          supplierName = parsed.supplierName;
        }
      }

      setExtractedData({
        receiptNumber: result.receiptNumber || '',
        poNumber: result.poNumber || '',
        supplierName,
        receivedDate: result.receivedDate || '',
        totalAmount: result.totalAmount || '',
        detectedItems: result.detectedItems || []
      });

      // Auto-switch to items tab if items are detected
      if (result.detectedItems && result.detectedItems.length > 0) {
        setActiveTab('items');
      }

      setProgress(100);
      setProgressStatus(
        result.engine === 'server'
          ? 'วิเคราะห์ข้อมูลสำเร็จด้วย AI Server (Qwen2.5-VL)'
          : result.engine === 'local_server' || result.engine === 'gemini_spark'
          ? 'วิเคราะห์ข้อมูลสำเร็จด้วย Local AI Server (Mac M4 / NAS)'
          : result.engine === 'local_fallback'
          ? 'วิเคราะห์ข้อมูลสำเร็จด้วย Local Fallback (Tesseract.js)'
          : 'วิเคราะห์ข้อมูลสำเร็จด้วย Local Browser Engine'
      );
    } catch (err) {
      console.error('OCR Processing error:', err);
      setScanError('การอ่านข้อความล้มเหลว: ' + (err?.message || 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      setIsScanning(false);
    }
  }, [imageUrl, selectedEngine]);

  const wasOpenRef = useRef(false);

  // Run automatically when modal opens with an image
  useEffect(() => {
    if (isOpen && imageUrl) {
      startScan();
    } else if (!isOpen && wasOpenRef.current) {
      // Reset states only when transitioning from open to closed
      setIsScanning(false);
      setProgress(0);
      setProgressStatus('');
      setScanError('');
      setConfidence(null);
      setUsedEngine(null);
      setExtractedData({
        receiptNumber: '',
        poNumber: '',
        supplierName: '',
        receivedDate: '',
        totalAmount: '',
        detectedItems: []
      });
      setRawText('');
      setCopySuccess(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, imageUrl, startScan]);

  if (!isOpen) return null;

  const handleFieldChange = (field, value) => {
    setExtractedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCopyRawText = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rawText);
      } else {
        // Fallback for older browsers / testing
        const textarea = document.createElement('textarea');
        textarea.value = rawText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDownloadTxt = () => {
    const filename = `receipt-ocr-${extractedData.receiptNumber || 'document'}.txt`;
    downloadTextFile(filename, rawText);
  };

  const handleApply = () => {
    if (onApplyData) {
      onApplyData({
        ...extractedData,
        rawText,
        confidence,
        engine: usedEngine
      });
    }
    onClose();
  };

  return (
    <div
      data-testid="receipt-ocr-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isScanning) {
          onClose();
        }
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #047857'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4em' }}>🔍</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: 'bold' }}>
                อ่านข้อมูลใบเสร็จอัตโนมัติ (Receipt OCR & Auto-Fill)
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', opacity: 0.9 }}>
                แปลงข้อความจากภาพใบเสร็จ ตรวจจับเลขที่บิล, PO, วันที่, และผู้จำหน่ายอัตโนมัติ
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '1.2em',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            title="ปิดหน้าต่าง"
          >
            ✕
          </button>
        </div>

        {/* AI Engine Status & Selector Bar */}
        <div
          style={{
            padding: '10px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          {/* AI Status Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.82em', color: '#475569', fontWeight: '600' }}>สถานะระบบ AI:</span>
            {serverStatus === 'online' ? (
              <span
                data-testid="ai-status-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: '0.82em',
                  fontWeight: 'bold',
                  background: '#dcfce7',
                  color: '#15803d',
                  border: '1px solid #86efac'
                }}
              >
                <span>🟢</span>
                <span>AI Server (Qwen2.5-VL)</span>
              </span>
            ) : serverStatus === 'checking' ? (
              <span
                data-testid="ai-status-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: '0.82em',
                  fontWeight: '600',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #cbd5e1'
                }}
              >
                <span>🔄</span>
                <span>กำลังตรวจสอบ AI Server...</span>
              </span>
            ) : (
              <span
                data-testid="ai-status-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: '0.82em',
                  fontWeight: 'bold',
                  background: '#fef3c7',
                  color: '#b45309',
                  border: '1px solid #fde68a'
                }}
              >
                <span>🟡</span>
                <span>Local Engine (Tesseract.js)</span>
              </span>
            )}
          </div>

          {/* Engine Mode Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="ai-engine-select" style={{ fontSize: '0.82em', color: '#475569', fontWeight: '600' }}>
              เครื่องยนต์ OCR:
            </label>
            <select
              id="ai-engine-select"
              value={selectedEngine}
              onChange={(e) => setSelectedEngine(e.target.value)}
              disabled={isScanning}
              style={{
                padding: '4px 10px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.82em',
                background: '#ffffff',
                color: '#1e293b',
                cursor: 'pointer',
                fontWeight: '600',
                outline: 'none'
              }}
            >
              <option value="auto">อัตโนมัติ (Auto)</option>
              <option value="server">Local AI Server (Mac / NAS)</option>
              <option value="local">Local Browser</option>
            </select>
            {usedEngine && (
              <span style={{ fontSize: '0.75em', color: '#64748b', marginLeft: '4px' }}>
                ({usedEngine === 'server' || usedEngine === 'local_server' ? '⚡ Local AI Server (Mac M4 / NAS)' : usedEngine === 'local_fallback' ? 'ใช้ Local Fallback' : 'ใช้ Local Browser'})
              </span>
            )}
          </div>
        </div>

        {/* Status / Progress Bar */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '0.85em' }}>
            <span style={{ fontWeight: '600', color: isScanning ? '#0284c7' : scanError ? '#dc2626' : '#059669' }}>
              {isScanning && '⏳ '}
              {!isScanning && !scanError && progress === 100 && '✅ '}
              {progressStatus || (isScanning ? 'กำลังประมวลผล...' : 'พร้อมใช้งาน')}
            </span>
            <span style={{ color: '#64748b', fontWeight: 'bold' }}>
              {progress}% {confidence !== null && `(ความมั่นใจ: ${Math.round(confidence)}%)`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: scanError
                  ? '#ef4444'
                  : 'linear-gradient(90deg, #10b981, #059669)',
                transition: 'width 0.3s ease',
                borderRadius: '4px'
              }}
            />
          </div>
          {scanError && (
            <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontSize: '0.85em' }}>
              ⚠️ {scanError}
            </div>
          )}
        </div>

        {/* Main Body: 2-Column Responsive Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(300px, 1fr) minmax(360px, 1.25fr)',
            gap: '16px',
            padding: '16px 20px',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {/* Left Column: Image Preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', fontSize: '0.9em', color: '#334155' }}>
                🖼️ ภาพถ่ายใบเสร็จต้นฉบับ
              </span>
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => setIsZoomed(true)}
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '0.78em',
                    cursor: 'pointer',
                    color: '#0f172a'
                  }}
                >
                  🔍 แตะเพื่อซูม
                </button>
              )}
            </div>

            <div
              onClick={() => imageUrl && setIsZoomed(true)}
              style={{
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                background: '#f8fafc',
                minHeight: '280px',
                maxHeight: '440px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: imageUrl ? 'zoom-in' : 'default',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Receipt Preview"
                  style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain' }}
                />
              ) : (
                <span style={{ color: '#94a3b8', fontSize: '0.9em' }}>ไม่มีรูปภาพที่เลือก</span>
              )}
            </div>
          </div>

          {/* Right Column: Extracted Fields & Raw Text Tabs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Extracted Form Inputs */}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              <div style={{ fontSize: '0.92em', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>📑 ข้อมูลที่ตรวจพบ (ตรวจสอบและแก้ไขได้):</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {/* Receipt Number */}
                <div>
                  <label htmlFor="ocr-receipt-number" style={{ display: 'block', fontSize: '0.8em', color: '#475569', marginBottom: '4px', fontWeight: '600' }}>
                    เลขที่ใบเสร็จ / INV:
                  </label>
                  <input
                    id="ocr-receipt-number"
                    type="text"
                    value={extractedData.receiptNumber}
                    onChange={(e) => handleFieldChange('receiptNumber', e.target.value)}
                    placeholder="เช่น INV-2025/001"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.9em',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* PO Number */}
                <div>
                  <label htmlFor="ocr-po-number" style={{ display: 'block', fontSize: '0.8em', color: '#475569', marginBottom: '4px', fontWeight: '600' }}>
                    เลขที่ PO (ถ้ามี):
                  </label>
                  <input
                    id="ocr-po-number"
                    type="text"
                    value={extractedData.poNumber}
                    onChange={(e) => handleFieldChange('poNumber', e.target.value)}
                    placeholder="เช่น PO-68001"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.9em',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Supplier Name */}
              <div>
                <label htmlFor="ocr-supplier-name" style={{ display: 'block', fontSize: '0.8em', color: '#475569', marginBottom: '4px', fontWeight: '600' }}>
                  บริษัทผู้จำหน่าย:
                </label>
                <input
                  id="ocr-supplier-name"
                  type="text"
                  list="supplier-suggestions-list"
                  value={extractedData.supplierName}
                  onChange={(e) => handleFieldChange('supplierName', e.target.value)}
                  placeholder="เช่น บริษัท เด็นทัลสยาม จำกัด"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '0.9em',
                    background: '#ffffff',
                    boxSizing: 'border-box'
                  }}
                />
                <datalist id="supplier-suggestions-list">
                  {supplierSuggestions.map((sup, idx) => (
                    <option key={idx} value={sup} />
                  ))}
                </datalist>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {/* Received Date */}
                <div>
                  <label htmlFor="ocr-received-date" style={{ display: 'block', fontSize: '0.8em', color: '#475569', marginBottom: '4px', fontWeight: '600' }}>
                    วันที่รับเข้า (ค.ศ.):
                  </label>
                  <input
                    id="ocr-received-date"
                    type="date"
                    value={extractedData.receivedDate}
                    onChange={(e) => handleFieldChange('receivedDate', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.9em',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Total Amount */}
                <div>
                  <label htmlFor="ocr-total-amount" style={{ display: 'block', fontSize: '0.8em', color: '#475569', marginBottom: '4px', fontWeight: '600' }}>
                    ยอดเงินรวม (฿):
                  </label>
                  <input
                    id="ocr-total-amount"
                    type="number"
                    step="0.01"
                    value={extractedData.totalAmount}
                    onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.9em',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Accordion / Tabs for Raw Text & Detected Items */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px', marginBottom: '8px' }}>
                <button
                  type="button"
                  onClick={() => setActiveTab('raw')}
                  style={{
                    border: 'none',
                    background: activeTab === 'raw' ? '#e2e8f0' : 'transparent',
                    color: activeTab === 'raw' ? '#0f172a' : '#64748b',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  📋 ข้อความดิบที่อ่านได้ (Raw Text)
                </button>
                {extractedData.detectedItems?.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('items')}
                    style={{
                      border: 'none',
                      background: activeTab === 'items' ? '#e2e8f0' : 'transparent',
                      color: activeTab === 'items' ? '#0f172a' : '#64748b',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    📦 รายการสินค้าที่ตรวจพบ ({extractedData.detectedItems.length})
                  </button>
                )}
              </div>

              {activeTab === 'raw' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '6px' }}>
                    <button
                      type="button"
                      onClick={handleCopyRawText}
                      disabled={!rawText}
                      style={{
                        padding: '4px 10px',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.8em',
                        cursor: rawText ? 'pointer' : 'not-allowed',
                        color: copySuccess ? '#059669' : '#334155',
                        fontWeight: '600'
                      }}
                    >
                      {copySuccess ? '✅ คัดลอกแล้ว!' : '📋 คัดลอกข้อความ'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadTxt}
                      disabled={!rawText}
                      style={{
                        padding: '4px 10px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '6px',
                        fontSize: '0.8em',
                        cursor: rawText ? 'pointer' : 'not-allowed',
                        color: '#1d4ed8',
                        fontWeight: '600'
                      }}
                    >
                      💾 ดาวน์โหลดเป็นไฟล์ .txt
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={rawText}
                    placeholder="ข้อความที่อ่านได้จากภาพใบเสร็จจะปรากฏที่นี่..."
                    style={{
                      width: '100%',
                      minHeight: '130px',
                      height: '130px',
                      padding: '10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.82em',
                      fontFamily: 'monospace',
                      background: '#fafafa',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              ) : (
                <div style={{ overflowX: 'auto', maxHeight: '180px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '6px 8px' }}>ชื่อรายการ</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>จำนวน</th>
                        <th style={{ padding: '6px 8px' }}>หน่วย</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>ราคา (฿)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedData.detectedItems.map((it, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 8px' }}>{it.item_name}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.quantity}</td>
                          <td style={{ padding: '6px 8px' }}>{it.unit}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.unit_price?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div
          style={{
            padding: '14px 20px',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <button
            type="button"
            onClick={startScan}
            disabled={isScanning || !imageUrl}
            style={{
              padding: '8px 16px',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              color: '#334155',
              cursor: isScanning || !imageUrl ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.88em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔄 สแกนใหม่
          </button>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isScanning}
              style={{
                padding: '8px 18px',
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                color: '#475569',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.88em'
              }}
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={isScanning}
              style={{
                padding: '8px 20px',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.88em',
                boxShadow: '0 4px 10px rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ✨ นำข้อมูลไปกรอกในฟอร์มอัตโนมัติ
            </button>
          </div>
        </div>
      </div>

      {/* High-res Image Zoom Modal */}
      {isZoomed && imageUrl && (
        <div
          onClick={() => setIsZoomed(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: '20px'
          }}
        >
          <img
            src={imageUrl}
            alt="Enlarged Receipt"
            style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: '8px' }}
          />
        </div>
      )}
    </div>
  );
};
